import { useContext, useState } from "react";

import { useFormikContext } from "formik";

import { Button, Header, Info } from "components/elements";
import {
  ImageUploaderField,
  ImageUploaderContext,
} from "components/form/fields";
import { Column, Row, Section, Subsection } from "components/layout";
import { IMAGE_ENVS } from "pages/TiVoApps/constants";

import { TEXTS } from "../constants";
import { PROVIDER_FIELDS } from "../fields";
import { ProviderForm } from "../types";
import { copyAssetsToProduction } from "../utils";
import styles from "./styles.scss";

const LogoSection = ({
  header,
  fields,
}: {
  header: string;
  fields: Array<
    (typeof PROVIDER_FIELDS.IMAGES)[keyof typeof PROVIDER_FIELDS.IMAGES]
  >;
}) => (
  <Subsection className={styles.subSection}>
    <Header bold uppercase color="secondary" type="h4">
      {header}
    </Header>
    {fields.map((fieldConfig) => (
      <Row key={fieldConfig.name}>
        <Column>
          <ImageUploaderField {...fieldConfig} />
        </Column>
        <Column>
          <ImageUploaderField
            {...fieldConfig}
            name={fieldConfig.name.replace(IMAGE_ENVS.STAGING, IMAGE_ENVS.PROD)}
          />
        </Column>
      </Row>
    ))}
  </Subsection>
);

export function TiVoAppFormMediaTab() {
  const {
    values: { assets },
    setFieldValue,
  } = useFormikContext<ProviderForm>();
  const { setImage } = useContext(ImageUploaderContext);
  const [isCopyingAssets, setIsCopyingAssets] = useState(false);
  const [copyError, setCopyError] = useState("");
  const handleClick = async () => {
    const stagingAssets = assets?.staging || {};

    try {
      setIsCopyingAssets(true);
      setCopyError("");
      await copyAssetsToProduction(stagingAssets, setImage, setFieldValue);
    } catch (error) {
      setCopyError(String(error));
    } finally {
      setIsCopyingAssets(false);
    }
  };

  return (
    <Section>
      {copyError && <Info type="error">{copyError}</Info>}
      <Row>
        <Column>
          <Header bold uppercase color="primary" type="h3">
            {TEXTS.MEDIA.STAGING}
          </Header>
        </Column>
        <Column className={styles.copyColumn}>
          <Header bold uppercase color="primary" type="h3">
            {TEXTS.MEDIA.PROD}
          </Header>
          <Button
            className={styles.copyButton}
            disabled={isCopyingAssets}
            processing={isCopyingAssets}
            type="green"
            dataTestId="copy-all-images-button"
            onClick={handleClick}
          >
            {TEXTS.MEDIA.COPY_BUTTON}
          </Button>
        </Column>
      </Row>
      <LogoSection
        header={TEXTS.MEDIA.SQUARE_LOGO_HEADER}
        fields={[
          PROVIDER_FIELDS.IMAGES.PRIMARY_BRANDING_LOGO_SQUARE,
          PROVIDER_FIELDS.IMAGES.PARTNER_SOURCE_LOGO_SQUARE,
        ]}
      />
      <LogoSection
        header={TEXTS.MEDIA.RECT_LOGO_HEADER}
        fields={[
          PROVIDER_FIELDS.IMAGES.PRIMARY_BRANDING_LOGO_RECTANGLE,
          PROVIDER_FIELDS.IMAGES.ATMOSPHERIC,
        ]}
      />
      <LogoSection
        header={TEXTS.MEDIA.WIDE_LOGO_HEADER}
        fields={[PROVIDER_FIELDS.IMAGES.PARTNER_SOURCE_LOGO_WIDE]}
      />
    </Section>
  );
}
